// Authentication Context - Supabase Integration
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authError, setAuthError] = useState(null);

    // Initialize auth state
    useEffect(() => {
        if (!supabase) {
            setLoading(false);
            return;
        }

        // Get initial session
        const initAuth = async () => {
            try {
                console.log('[Auth] Initializing auth...');
                const { data: { session }, error } = await supabase.auth.getSession();
                if (error) throw error;

                console.log('[Auth] Session result:', session ? `Found for ${session.user?.email}` : 'No session');
                setSession(session);
                setUser(session?.user ?? null);
            } catch (error) {
                console.error('[Auth] Initialization error:', error);
                setAuthError(error.message);
            } finally {
                setLoading(false);
            }
        };

        initAuth();

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, session) => {
                setSession(session);
                setUser(session?.user ?? null);
                setLoading(false);

                if (event === 'SIGNED_OUT') {
                    // Clear any local data on logout
                    localStorage.removeItem('calobite_user_profile');
                }
            }
        );

        return () => {
            subscription?.unsubscribe();
        };
    }, []);

    // Sign up with email and password
    const signUp = useCallback(async (email, password, metadata = {}) => {
        if (!supabase) return { error: { message: 'Auth not configured' } };

        setAuthError(null);
        try {
            const { data, error } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: metadata // Store additional user data like name
                }
            });

            if (error) throw error;
            return { data, error: null };
        } catch (error) {
            setAuthError(error.message);
            return { data: null, error };
        }
    }, []);

    // Sign in with email and password
    const signIn = useCallback(async (email, password) => {
        if (!supabase) return { error: { message: 'Auth not configured' } };

        setAuthError(null);
        try {
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password
            });

            if (error) throw error;
            return { data, error: null };
        } catch (error) {
            setAuthError(error.message);
            return { data: null, error };
        }
    }, []);

    // Sign in with OAuth (Google, Apple, etc.)
    const signInWithOAuth = useCallback(async (provider) => {
        if (!supabase) return { error: { message: 'Auth not configured' } };

        setAuthError(null);
        try {
            const { data, error } = await supabase.auth.signInWithOAuth({
                provider,
                options: {
                    redirectTo: window.location.origin
                }
            });

            if (error) throw error;
            return { data, error: null };
        } catch (error) {
            setAuthError(error.message);
            return { data: null, error };
        }
    }, []);

    // Sign out — Nuclear Reset
    // Both paths (authService.logout + AuthContext.signOut) must produce identical wipes.
    const signOut = useCallback(async () => {
        if (!supabase) return { error: { message: 'Auth not configured' } };

        setAuthError(null);
        try {
            const { fullNuclearLogout } = await import('../utils/nuclearReset');
            const email = localStorage.getItem('calobite_current_user');
            await fullNuclearLogout(email);
            return { error: null };
        } catch (error) {
            setAuthError(error.message);
            return { error };
        }
    }, []);

    // Update password
    const updatePassword = useCallback(async (newPassword) => {
        if (!supabase) return { error: { message: 'Auth not configured' } };

        setAuthError(null);
        try {
            const { data, error } = await supabase.auth.updateUser({
                password: newPassword
            });

            if (error) throw error;
            return { data, error: null };
        } catch (error) {
            setAuthError(error.message);
            return { data: null, error };
        }
    }, []);

    // Update user metadata (name, etc.)
    const updateProfile = useCallback(async (updates) => {
        if (!supabase) return { error: { message: 'Auth not configured' } };

        setAuthError(null);
        try {
            const { data, error } = await supabase.auth.updateUser({
                data: updates
            });

            if (error) throw error;
            return { data, error: null };
        } catch (error) {
            setAuthError(error.message);
            return { data: null, error };
        }
    }, []);

    // Send password reset email
    const resetPassword = useCallback(async (email) => {
        if (!supabase) return { error: { message: 'Auth not configured' } };

        setAuthError(null);
        try {
            const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/reset-password`
            });

            if (error) throw error;
            return { data, error: null };
        } catch (error) {
            setAuthError(error.message);
            return { data: null, error };
        }
    }, []);

    // Update email
    const updateEmail = useCallback(async (newEmail) => {
        if (!supabase) return { error: { message: 'Auth not configured' } };

        setAuthError(null);
        try {
            const { data, error } = await supabase.auth.updateUser({
                email: newEmail
            });

            if (error) throw error;
            return { data, error: null };
        } catch (error) {
            setAuthError(error.message);
            return { data: null, error };
        }
    }, []);

    // Check if auth is available (Supabase configured)
    const isAuthAvailable = !!supabase;

    // Get user display name
    const getUserDisplayName = useCallback(() => {
        if (!user) return 'Guest';
        return user.user_metadata?.full_name ||
            user.user_metadata?.name ||
            user.email?.split('@')[0] ||
            'User';
    }, [user]);

    // Get user avatar
    const getUserAvatar = useCallback(() => {
        if (!user) return null;
        return user.user_metadata?.avatar_url ||
            user.user_metadata?.picture ||
            null;
    }, [user]);

    const value = {
        // State
        user,
        session,
        loading,
        authError,
        isAuthenticated: !!user,
        isAuthAvailable,

        // Auth functions
        signUp,
        signIn,
        signInWithOAuth,
        signOut,
        updatePassword,
        updateProfile,
        updateEmail,
        resetPassword,

        // Helpers
        getUserDisplayName,
        getUserAvatar,
        clearError: () => setAuthError(null)
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
